import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Plus, Download, Search, X, Check, Trash2, Loader2, KeyRound } from "lucide-react";
import { apiUrl } from "../../../utils/config";
import { invalidateTranslationCache } from "../../../hooks/useTranslations";

const SUPPORTED_LANGS = [
  { code: "en", name: "English",    flag: "🇬🇧" },
  { code: "es", name: "Spanish",    flag: "🇪🇸" },
  { code: "fr", name: "French",     flag: "🇫🇷" },
  { code: "ar", name: "Arabic",     flag: "🇸🇦" },
  { code: "de", name: "German",     flag: "🇩🇪" },
  { code: "zh", name: "Chinese",    flag: "🇨🇳" },
  { code: "pt", name: "Portuguese", flag: "🇵🇹" },
  { code: "ru", name: "Russian",    flag: "🇷🇺" },
  { code: "ja", name: "Japanese",   flag: "🇯🇵" },
  { code: "hi", name: "Hindi",      flag: "🇮🇳" },
  { code: "it", name: "Italian",    flag: "🇮🇹" },
  { code: "ko", name: "Korean",     flag: "🇰🇷" },
  { code: "nl", name: "Dutch",      flag: "🇳🇱" },
  { code: "pl", name: "Polish",     flag: "🇵🇱" },
  { code: "tr", name: "Turkish",    flag: "🇹🇷" },
];

const STARTER_KEYS = [
  "store_name", "tagline", "menu_title", "about_title", "contact_title",
  "cart_title", "checkout_title", "add_to_cart", "order_now",
  "search_placeholder", "footer_text", "contact_us",
  "subscribe_placeholder", "subscribe_button", "out_of_stock", "free_shipping",
];

const normalize = (raw) => {
  const obj = raw?.data || raw || {};
  const result = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v && typeof v === "object" && !Array.isArray(v)) result[k] = v;
  }
  return result;
};

// ── Component ─────────────────────────────────────────────────────────────────
const TranslationsPage = () => {
  const [translations, setTranslations] = useState({});   // { key: { lang: value } }
  const [selectedKey, setSelectedKey]   = useState(null);
  const [activeLang, setActiveLang]     = useState("en");
  const [search, setSearch]             = useState("");
  const [newKey, setNewKey]             = useState("");
  const [addingKey, setAddingKey]       = useState(false);
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);
  const [saved, setSaved]               = useState(false);
  const [error, setError]               = useState(null);

  const allKeys      = Object.keys(translations).sort();
  const q            = search.toLowerCase();
  const filteredKeys = allKeys.filter((k) => !q || k.toLowerCase().includes(q));

  // ── Load ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const res  = await axios.get(`${apiUrl}/translations`);
        const data = normalize(res.data);
        if (Object.keys(data).length === 0) {
          const seed = {};
          STARTER_KEYS.forEach((k) => { seed[k] = {}; });
          setTranslations(seed);
          setSelectedKey(STARTER_KEYS[0]);
        } else {
          setTranslations(data);
          setSelectedKey(Object.keys(data).sort()[0] || null);
        }
      } catch {
        setError("Could not load translations.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ── Key ops ───────────────────────────────────────────────────────────────
  const addKey = useCallback(() => {
    const key = newKey.trim().toLowerCase().replace(/\s+/g, "_");
    if (!key || key in translations) return;
    setTranslations((prev) => ({ ...prev, [key]: {} }));
    setSelectedKey(key);
    setNewKey("");
    setAddingKey(false);
    setSaved(false);
  }, [newKey, translations]);

  const deleteKey = useCallback(async (key) => {
    // 1. Remove from local state immediately
    setTranslations((prev) => {
      const { [key]: _, ...rest } = prev;
      return rest;
    });
    setSelectedKey((prev) => {
      if (prev !== key) return prev;
      const remaining = Object.keys(translations).filter((k) => k !== key).sort();
      return remaining[0] || null;
    });
    // 2. Persist to backend right away — no manual Save needed
    try {
      await axios.delete(`${apiUrl}/translations/${encodeURIComponent(key)}`);
      invalidateTranslationCache();
    } catch {
      setError(`Failed to delete "${key}". Please try again.`);
    }
  }, [translations]);

  // ── Value edit ────────────────────────────────────────────────────────────
  const setValue = useCallback((key, lang, value) => {
    setTranslations((prev) => ({
      ...prev,
      [key]: { ...prev[key], [lang]: value },
    }));
    setSaved(false);
  }, []);

  // ── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${apiUrl}/translations`, translations);
      invalidateTranslationCache();
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch {
      setError("Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleExport = () => {
    const blob = new Blob([JSON.stringify(translations, null, 2)], { type: "application/json" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url; a.download = "translations.json"; a.click();
    URL.revokeObjectURL(url);
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center py-32 text-gray-400">
        <Loader2 size={28} className="animate-spin mr-3" />
        <span className="text-sm font-medium">Loading translations…</span>
      </div>
    );
  }

  const currentValue = selectedKey ? (translations[selectedKey]?.[activeLang] ?? "") : "";
  const activeMeta   = SUPPORTED_LANGS.find((l) => l.code === activeLang);

  // How many langs have a value for the selected key
  const filledCount = selectedKey
    ? SUPPORTED_LANGS.filter((l) => translations[selectedKey]?.[l.code]).length
    : 0;

  return (
    <div className="space-y-5">

      {/* ── Header ── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Translations</h1>
          <p className="text-sm text-gray-400 mt-0.5">Click a key, pick a language, edit its value.</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleExport}
            className="border border-gray-200 text-gray-600 px-4 py-2 rounded-xl flex items-center gap-2 font-bold hover:bg-gray-50 transition text-sm"
          >
            <Download size={14} /> Export
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className={`px-4 py-2 rounded-xl flex items-center gap-2 font-bold transition text-sm shadow-lg active:scale-95 disabled:opacity-50
              ${saved ? "bg-emerald-500 shadow-emerald-200 text-white" : "bg-orange-500 shadow-orange-200 text-white hover:bg-orange-600"}`}
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
            {saving ? "Saving…" : saved ? "Saved!" : "Save"}
          </button>
        </div>
      </div>

      {/* ── Error ── */}
      {error && (
        <div className="flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
          <X size={15} className="flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError(null)}><X size={14} /></button>
        </div>
      )}

      {/* ── Main split layout ── */}
      <div className="flex gap-4" style={{ minHeight: 560 }}>

        {/* ── Left: key list ── */}
        <div className="w-56 flex-shrink-0 bg-white rounded-2xl border border-gray-100 shadow-sm flex flex-col overflow-hidden">

          {/* Search + add */}
          <div className="p-3 border-b border-gray-100 space-y-2">
            <div className="relative">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search keys…"
                className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-orange-400"
              />
            </div>

            {addingKey ? (
              <div className="flex gap-1">
                <input
                  autoFocus
                  value={newKey}
                  onChange={(e) => setNewKey(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter")  addKey();
                    if (e.key === "Escape") { setAddingKey(false); setNewKey(""); }
                  }}
                  placeholder="key_name"
                  className="flex-1 min-w-0 border border-orange-300 rounded-lg px-2 py-1.5 text-xs font-mono outline-none focus:ring-2 focus:ring-orange-400"
                />
                <button onClick={addKey} className="bg-orange-500 text-white px-2 py-1.5 rounded-lg text-xs font-bold hover:bg-orange-600 transition">
                  Add
                </button>
              </div>
            ) : (
              <button
                onClick={() => setAddingKey(true)}
                className="w-full flex items-center justify-center gap-1 py-1.5 border-2 border-dashed border-gray-200 rounded-lg text-xs font-bold text-gray-400 hover:border-orange-300 hover:text-orange-500 transition"
              >
                <Plus size={12} /> New Key
              </button>
            )}
          </div>

          {/* Keys */}
          <div className="flex-1 overflow-y-auto divide-y divide-gray-50">
            {filteredKeys.length === 0 && (
              <p className="text-center text-xs text-gray-400 py-8">No keys found</p>
            )}
            {filteredKeys.map((key) => {
              const filled = SUPPORTED_LANGS.filter((l) => translations[key]?.[l.code]).length;
              return (
                <button
                  key={key}
                  onClick={() => setSelectedKey(key)}
                  className={`w-full text-left px-3 py-2.5 transition group flex items-center justify-between gap-2
                    ${selectedKey === key ? "bg-orange-50" : "hover:bg-gray-50"}`}
                >
                  <span className={`text-xs font-mono truncate ${selectedKey === key ? "text-orange-700 font-bold" : "text-gray-600"}`}>
                    {key}
                  </span>
                  {filled > 0 && (
                    <span className={`flex-shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full
                      ${selectedKey === key ? "bg-orange-200 text-orange-700" : "bg-gray-100 text-gray-400"}`}>
                      {filled}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="px-3 py-2 border-t border-gray-50 text-[10px] text-gray-400">
            {allKeys.length} keys
          </div>
        </div>

        {/* ── Right: detail form ── */}
        <div className="flex-1 bg-white rounded-2xl border border-gray-100 shadow-sm flex flex-col overflow-hidden">

          {!selectedKey ? (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-300 gap-3">
              <KeyRound size={40} />
              <p className="text-sm font-medium text-gray-400">Select a key to start editing</p>
            </div>
          ) : (
            <>
              {/* Key header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <div>
                  <span className="text-xs font-mono font-bold text-gray-500 bg-gray-100 px-2.5 py-1 rounded-lg">
                    {selectedKey}
                  </span>
                  <span className="ml-3 text-xs text-gray-400">
                    {filledCount} / {SUPPORTED_LANGS.length} languages filled
                  </span>
                </div>
                <button
                  onClick={() => deleteKey(selectedKey)}
                  className="text-gray-300 hover:text-red-500 transition p-1"
                  title="Delete this key"
                >
                  <Trash2 size={15} />
                </button>
              </div>

              {/* Language tabs — inside the form, one active at a time */}
              <div className="px-6 pt-4 pb-0 flex items-center gap-1.5 flex-wrap border-b border-gray-100 pb-3">
                {SUPPORTED_LANGS.map((lang) => {
                  const hasValue = !!translations[selectedKey]?.[lang.code];
                  const isActive = activeLang === lang.code;
                  return (
                    <button
                      key={lang.code}
                      onClick={() => setActiveLang(lang.code)}
                      className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold transition relative
                        ${isActive
                          ? "bg-orange-500 text-white shadow"
                          : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                        }`}
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.code.toUpperCase()}</span>
                      {hasValue && !isActive && (
                        <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-emerald-400" />
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Single language input */}
              <div className="flex-1 px-6 py-6">
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-3">
                  <span className="text-xl">{activeMeta?.flag}</span>
                  {activeMeta?.name}
                </label>
                <textarea
                  key={`${selectedKey}-${activeLang}`}
                  value={currentValue}
                  onChange={(e) => setValue(selectedKey, activeLang, e.target.value)}
                  placeholder={`${activeMeta?.name} translation for "${selectedKey}"…`}
                  rows={4}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-orange-400 transition resize-none placeholder-gray-300"
                />
                <p className="mt-2 text-xs text-gray-400">
                  {currentValue.length > 0
                    ? `${currentValue.length} characters`
                    : "Empty — will fall back to English on the frontend"}
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TranslationsPage;
